/**
 * Submission Triage — Global Filters drawer (OR-979).
 *
 * Per Andressa's Figma (re-laid out in OR-1669): Broker & Brokerage,
 * Dates (Renewals presets + Custom Dates range), Submission type, Status,
 * #Communities (facility count range), Underwriters (UW + Analyst),
 * Deductible (expiring deductible range). Submission title is NOT here —
 * it lives in Search. Broker, Submission month and Probability to bind are
 * in the design but have no backend filter yet, so they are left out.
 *
 * Renewals presets and Custom Dates both write `renewal_date`, so they are
 * mutually exclusive. A preset is resolved from today when the request is
 * built, so a saved view keeps meaning "the next 30 days" tomorrow.
 *
 * Every clause maps to a real backend query param (see `filtersToParams`
 * → `ListSubmissionsParams`). Brokerage/UW/Analyst keep both the id (sent
 * to the API) and a label (shown in the chips). Deferred apply: edits
 * stage a local draft; "Show results" commits, "Clear All" resets.
 */
import { useEffect, useMemo, useState } from "react";
import { useFilter } from "react-aria";
import { Dialog as AriaDialog, Modal as AriaModal, ModalOverlay as AriaModalOverlay } from "react-aria-components";
import { type CalendarDate, getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { X } from "@untitledui/icons";
import { Button, Checkbox, DateRangePicker, InputNumber, SearchSelect, cx } from "@tricura/atlas-ui";
import { listUsers, userLabel } from "../../api";
import type { ListSubmissionsParams } from "../../api";
import { SUBMISSION_STATUSES, type SubmissionStatus, type SubmissionType } from "../../types/submission";
import { useBrokerageOptions } from "../../hooks/use-brokerage-options";
import { useDebouncedValue } from "../../hooks/use-debounced-value";

/** A picked option that carries its id (sent to the API) + label (chips). */
export interface FilterOption {
    id: string;
    label: string;
}

export interface TriageFilters {
    types: string[];
    /** Submission status (OR-1467). The list endpoint already accepts
     *  repeated `status=` params, so this needed no backend work. */
    statuses: string[];
    brokerage: FilterOption | null;
    /** Days from today (OR-1669), or "" when a custom range (or nothing) is set. */
    renewalPreset: RenewalPreset | "";
    renewalStart: string; // ISO YYYY-MM-DD, "" = unset
    renewalEnd: string;
    uw: FilterOption | null;
    analyst: FilterOption | null;
    facilityCountMin: string;
    facilityCountMax: string;
    expiringDeductibleMin: string;
    expiringDeductibleMax: string;
}

// eslint-disable-next-line react-refresh/only-export-components -- shared filter shape co-located with the drawer
export const EMPTY_FILTERS: TriageFilters = {
    types: [],
    statuses: [],
    brokerage: null,
    renewalPreset: "",
    renewalStart: "",
    renewalEnd: "",
    uw: null,
    analyst: null,
    facilityCountMin: "",
    facilityCountMax: "",
    expiringDeductibleMin: "",
    expiringDeductibleMax: "",
};

const TYPES: SubmissionType[] = ["New Business", "Renewal", "Endorsement", "Tail"];
const STATUSES: readonly SubmissionStatus[] = SUBMISSION_STATUSES;

// eslint-disable-next-line react-refresh/only-export-components -- shared filter shape co-located with the drawer
export const RENEWAL_PRESETS = ["90", "60", "30"] as const;
export type RenewalPreset = (typeof RENEWAL_PRESETS)[number];

// eslint-disable-next-line react-refresh/only-export-components -- shared filter shape co-located with the drawer
export const RENEWAL_PRESET_LABELS: Record<RenewalPreset, string> = {
    "90": "90 days",
    "60": "60 days",
    "30": "30 days",
};

/** The inclusive `[today, today + days]` window a preset stands for. */
// eslint-disable-next-line react-refresh/only-export-components -- shared helper co-located with the drawer
export function renewalPresetRange(preset: RenewalPreset, from: CalendarDate = today(getLocalTimeZone())): { start: string; end: string } {
    return { start: from.toString(), end: from.add({ days: Number(preset) }).toString() };
}

/** Committed filters → real backend query params. `from` is injectable
 *  so the preset window is testable; it defaults to today. */
// eslint-disable-next-line react-refresh/only-export-components -- shared helper co-located with the drawer
export function filtersToParams(f: TriageFilters, from?: CalendarDate): Partial<ListSubmissionsParams> {
    const p: Partial<ListSubmissionsParams> = {};
    if (f.types.length) p.submissionTypes = f.types as SubmissionType[];
    if (f.statuses.length) p.status = f.statuses as SubmissionStatus[];
    if (f.brokerage) p.brokerageId = f.brokerage.id;
    if (f.uw) p.assigneeId = Number(f.uw.id);
    if (f.analyst) p.approverId = Number(f.analyst.id);
    if (f.renewalPreset) {
        const range = renewalPresetRange(f.renewalPreset, from);
        p.minRenewalDate = range.start;
        p.maxRenewalDate = range.end;
    } else {
        if (f.renewalStart) p.minRenewalDate = f.renewalStart;
        if (f.renewalEnd) p.maxRenewalDate = f.renewalEnd;
    }
    if (f.facilityCountMin) p.minNumFacilities = Number(f.facilityCountMin);
    if (f.facilityCountMax) p.maxNumFacilities = Number(f.facilityCountMax);
    if (f.expiringDeductibleMin) p.minExpiringDeductible = Number(f.expiringDeductibleMin);
    if (f.expiringDeductibleMax) p.maxExpiringDeductible = Number(f.expiringDeductibleMax);
    return p;
}

// eslint-disable-next-line react-refresh/only-export-components -- shared helper co-located with the drawer
export function countActiveFilters(f: TriageFilters): number {
    let n = f.types.length + f.statuses.length;
    if (f.brokerage) n += 1;
    if (f.uw) n += 1;
    if (f.analyst) n += 1;
    if (f.renewalPreset || f.renewalStart || f.renewalEnd) n += 1;
    if (f.facilityCountMin || f.facilityCountMax) n += 1;
    if (f.expiringDeductibleMin || f.expiringDeductibleMax) n += 1;
    return n;
}

interface FiltersDrawerProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    value: TriageFilters;
    matchCount: number;
    onApply: (next: TriageFilters) => void;
    onClearAll: () => void;
}

export function FiltersDrawer({ isOpen, onOpenChange, value, matchCount, onApply, onClearAll }: FiltersDrawerProps) {
    return (
        <AriaModalOverlay
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            isDismissable
            className={({ isEntering, isExiting }) =>
                cx("bg-overlay/70 fixed inset-0 z-50", isEntering && "animate-in fade-in ease-out", isExiting && "animate-out fade-out ease-in")
            }
        >
            <AriaModal
                className={({ isEntering, isExiting }) =>
                    cx(
                        "bg-primary outline-hidden fixed bottom-0 right-0 top-0 w-[460px] max-w-full shadow-2xl",
                        isEntering && "animate-in slide-in-from-right ease-out",
                        isExiting && "animate-out slide-out-to-right ease-in",
                    )
                }
            >
                <AriaDialog className="outline-hidden flex h-full flex-col">
                    {({ close }) => (
                        <DrawerBody
                            value={value}
                            matchCount={matchCount}
                            onApply={(next) => {
                                onApply(next);
                                close();
                            }}
                            onClearAll={() => {
                                onClearAll();
                                close();
                            }}
                            close={close}
                        />
                    )}
                </AriaDialog>
            </AriaModal>
        </AriaModalOverlay>
    );
}

interface BodyProps {
    close: () => void;
    value: TriageFilters;
    matchCount: number;
    onApply: (next: TriageFilters) => void;
    onClearAll: () => void;
}

/**
 * Server-side search-as-you-type for the UW / Analyst selects (same pattern
 * as the UW Information Brokerage section): typing re-queries `/v2/user/`
 * with the debounced term. The endpoint doesn't implement `search` yet, so
 * we also narrow client-side; once the backend matches, that narrowing
 * becomes a no-op. The selected option stays pinned so the trigger keeps
 * its label even when the current results don't include it.
 */
function useUserOptions(selected: FilterOption | null) {
    const [results, setResults] = useState<FilterOption[]>([]);
    const [query, setQuery] = useState("");
    const debouncedQuery = useDebouncedValue(query.trim(), 300);
    // Same matcher SearchSelect uses internally — accent/case-insensitive
    // ("debora" matches "Débora").
    const { contains } = useFilter({ sensitivity: "base" });

    useEffect(() => {
        let cancelled = false;
        void listUsers({ search: debouncedQuery || undefined })
            .then((rows) => !cancelled && setResults(rows.map((u) => ({ id: String(u.id), label: userLabel(u) }))))
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [debouncedQuery]);

    const items = useMemo(() => {
        const matched = debouncedQuery ? results.filter((o) => contains(o.label, debouncedQuery)) : results;
        return selected && !matched.some((o) => o.id === selected.id) ? [selected, ...matched] : matched;
    }, [results, debouncedQuery, selected, contains]);

    return { items, onSearchChange: setQuery };
}

function DrawerBody({ close, value, matchCount, onApply, onClearAll }: BodyProps) {
    const [draft, setDraft] = useState<TriageFilters>(value);
    const brokerageOptions = useBrokerageOptions(draft.brokerage);
    const uwOptions = useUserOptions(draft.uw);
    const analystOptions = useUserOptions(draft.analyst);

    const update = <K extends keyof TriageFilters>(key: K, val: TriageFilters[K]) => {
        setDraft((prev) => ({ ...prev, [key]: val }));
    };
    const toggleType = (t: string) => {
        const set = new Set(draft.types);
        if (set.has(t)) set.delete(t);
        else set.add(t);
        update("types", Array.from(set));
    };
    const toggleStatus = (st: string) => {
        const set = new Set(draft.statuses);
        if (set.has(st)) set.delete(st);
        else set.add(st);
        update("statuses", Array.from(set));
    };
    // Preset and custom range write the same two params, so they are
    // mutually exclusive: setting one clears the other.
    const setRenewalPreset = (preset: RenewalPreset) => {
        setDraft((prev) => ({
            ...prev,
            renewalPreset: prev.renewalPreset === preset ? "" : preset,
            renewalStart: "",
            renewalEnd: "",
        }));
    };
    const setRenewalRange = (start: string, end: string) => {
        setDraft((prev) => ({ ...prev, renewalPreset: "", renewalStart: start, renewalEnd: end }));
    };
    const hasFilters = countActiveFilters(draft) > 0;

    const renewalValue = useMemo(
        () => (draft.renewalStart && draft.renewalEnd ? { start: parseDate(draft.renewalStart), end: parseDate(draft.renewalEnd) } : null),
        [draft.renewalStart, draft.renewalEnd],
    );

    return (
        <>
            <div className="border-secondary relative flex flex-col gap-0.5 border-b px-6 py-5">
                <h2 className="text-primary text-base font-semibold leading-6">Filters</h2>
                <p className="text-tertiary text-sm leading-5">Refine results across the table.</p>
                <button
                    type="button"
                    onClick={close}
                    aria-label="Close"
                    data-autotest="triage-filters-button-close"
                    className="text-tertiary hover:text-primary size-8 hover:bg-secondary absolute right-3 top-3 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg"
                >
                    <X aria-hidden="true" className="size-5 stroke-[2.25px]" />
                </button>
            </div>

            <div className="atlas-scrollbar bg-secondary flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
                <Card title="Broker & Brokerage">
                    <Field label="Brokerage">
                        <SearchSelect
                            size="sm"
                            aria-label="Brokerage"
                            placeholder="Search and Select Brokerage"
                            data-autotest="triage-filters-select-brokerage"
                            items={brokerageOptions.items}
                            selectedKey={draft.brokerage?.id ?? null}
                            onSearchChange={brokerageOptions.onSearchChange}
                            onSelectionChange={(k) => update("brokerage", k ? (brokerageOptions.items.find((b) => b.id === k) ?? null) : null)}
                        >
                            {(item) => <SearchSelect.Item id={item.id}>{item.label}</SearchSelect.Item>}
                        </SearchSelect>
                    </Field>
                </Card>

                <Card title="Dates">
                    {/* Checkbox look, single-select behavior: the design
                      * allows one preset at a time. */}
                    <Field label="Renewals">
                        <div className="flex flex-wrap gap-2">
                            {RENEWAL_PRESETS.map((preset) => (
                                <CheckboxChip
                                    key={preset}
                                    label={RENEWAL_PRESET_LABELS[preset]}
                                    checked={draft.renewalPreset === preset}
                                    onChange={() => setRenewalPreset(preset)}
                                    dataAutotest={`triage-filters-checkbox-renewal-${preset}`}
                                />
                            ))}
                        </div>
                    </Field>
                    <Field label="Custom Dates">
                        <DateRangePicker
                            aria-label="Renewal date"
                            size="sm"
                            value={renewalValue}
                            data-autotest="triage-filters-datepicker-renewal-date"
                            onChange={(range) => setRenewalRange(range?.start ? range.start.toString() : "", range?.end ? range.end.toString() : "")}
                        />
                    </Field>
                </Card>

                <Card title="Submission type">
                    <div className="flex flex-wrap gap-2">
                        {TYPES.map((t) => (
                            <CheckboxChip key={t} label={t} checked={draft.types.includes(t)} onChange={() => toggleType(t)} dataAutotest={`triage-filters-checkbox-${t}`} />
                        ))}
                    </div>
                </Card>

                {/* OR-1467: filtering by where a submission stands, not
                  * just what kind it is. Its own card since OR-1669. */}
                <Card title="Status">
                    <div className="flex flex-wrap gap-2">
                        {STATUSES.map((st) => (
                            <CheckboxChip
                                key={st}
                                label={st}
                                checked={draft.statuses.includes(st)}
                                onChange={() => toggleStatus(st)}
                                dataAutotest={`triage-filters-checkbox-status-${st}`}
                            />
                        ))}
                    </div>
                </Card>

                <Card title="#Communities">
                    <NumberMinMax
                        label="Facility count"
                        minValue={draft.facilityCountMin}
                        maxValue={draft.facilityCountMax}
                        onMin={(v) => update("facilityCountMin", v)}
                        onMax={(v) => update("facilityCountMax", v)}
                    />
                </Card>

                <Card title="Underwriters">
                    <Field label="UW">
                        <SearchSelect
                            size="sm"
                            aria-label="UW"
                            placeholder="Select UW"
                            data-autotest="triage-filters-select-uw"
                            items={uwOptions.items}
                            selectedKey={draft.uw?.id ?? null}
                            onSearchChange={uwOptions.onSearchChange}
                            onSelectionChange={(k) => update("uw", k ? (uwOptions.items.find((u) => u.id === k) ?? null) : null)}
                        >
                            {(item) => <SearchSelect.Item id={item.id}>{item.label}</SearchSelect.Item>}
                        </SearchSelect>
                    </Field>
                    <Field label="Analyst">
                        <SearchSelect
                            size="sm"
                            aria-label="Analyst"
                            placeholder="Select Analyst"
                            data-autotest="triage-filters-select-analyst"
                            items={analystOptions.items}
                            selectedKey={draft.analyst?.id ?? null}
                            onSearchChange={analystOptions.onSearchChange}
                            onSelectionChange={(k) => update("analyst", k ? (analystOptions.items.find((u) => u.id === k) ?? null) : null)}
                        >
                            {(item) => <SearchSelect.Item id={item.id}>{item.label}</SearchSelect.Item>}
                        </SearchSelect>
                    </Field>
                </Card>

                <Card title="Deductible">
                    <MoneyMinMax
                        label="Expiring deductible"
                        minValue={draft.expiringDeductibleMin}
                        maxValue={draft.expiringDeductibleMax}
                        onMin={(v) => update("expiringDeductibleMin", v)}
                        onMax={(v) => update("expiringDeductibleMax", v)}
                    />
                </Card>
            </div>

            <div className="border-secondary bg-primary flex items-center justify-between gap-3 border-t px-6 py-4">
                <div className="flex flex-col">
                    <span className="text-primary text-sm font-semibold">{matchCount} Results</span>
                    <span className="text-tertiary text-xs">match these filters</span>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        color="secondary"
                        size="sm"
                        data-autotest="triage-filters-button-clear-all"
                        onClick={() => {
                            setDraft(EMPTY_FILTERS);
                            onClearAll();
                        }}
                    >
                        Clear All
                    </Button>
                    <Button color="primary" size="sm" isDisabled={!hasFilters} onClick={() => onApply(draft)} data-autotest="triage-filters-button-apply">
                        Show results
                    </Button>
                </div>
            </div>
        </>
    );
}

// ─────────────────────── Reusable primitives ───────────────────────

function Card({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="border-secondary bg-primary flex flex-col gap-3 rounded-xl border p-4">
            <h3 className="text-primary text-sm font-semibold">{title}</h3>
            {children}
        </section>
    );
}

function Field({ label, children}: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-secondary text-sm font-medium">{label}</span>
            {children}
        </div>
    );
}

function CheckboxChip({ label, checked, onChange, dataAutotest }: { label: string; checked: boolean; onChange: () => void; dataAutotest?: string }) {
    return (
        <label
            data-autotest={dataAutotest}
            className={cx(
                "border-secondary bg-primary inline-flex h-6 cursor-pointer items-center gap-1.5 rounded-md border pl-1 pr-2 text-xs font-medium transition duration-100 ease-linear",
                checked ? "border-brand text-primary" : "text-secondary hover:bg-secondary",
            )}
        >
            <Checkbox isSelected={checked} onChange={onChange} aria-label={label}/>
            {label}
        </label>
    );
}

function NumberMinMax({
    label,
    minValue,
    maxValue,
    onMin,
    onMax,
}: {
    label: string;
    minValue: string;
    maxValue: string;
    onMin: (v: string) => void;
    onMax: (v: string) => void;
}) {
    return (
        <Field label={label}>
            <div className="grid grid-cols-2 gap-3">
                <InputNumber
                    size="sm"
                    aria-label={`${label} min`}
                    placeholder="Min"
                    data-autotest="triage-filters-input-facility-count-min"
                    value={minValue ? Number(minValue) : undefined}
                    onChange={(n) => onMin(Number.isFinite(n) ? String(n) : "")}
                />
                <InputNumber
                    size="sm"
                    aria-label={`${label} max`}
                    placeholder="Max"
                    data-autotest="triage-filters-input-facility-count-max"
                    value={maxValue ? Number(maxValue) : undefined}
                    onChange={(n) => onMax(Number.isFinite(n) ? String(n) : "")}
                />
            </div>
        </Field>
    );
}

function MoneyMinMax({
    label,
    minValue,
    maxValue,
    onMin,
    onMax,
}: {
    label: string;
    minValue: string;
    maxValue: string;
    onMin: (v: string) => void;
    onMax: (v: string) => void;
}) {
    return (
        <Field label={label}>
            <div className="grid grid-cols-2 gap-3">
                <InputNumber
                    size="sm"
                    aria-label={`${label} min`}
                    placeholder="$ Min"
                    data-autotest="triage-filters-input-expiring-deductible-min"
                    formatOptions={{ style: "currency", currency: "USD", maximumFractionDigits: 0 }}
                    value={minValue ? Number(minValue) : undefined}
                    onChange={(n) => onMin(Number.isFinite(n) ? String(n) : "")}
                />
                <InputNumber
                    size="sm"
                    aria-label={`${label} max`}
                    data-autotest="triage-filters-input-expiring-deductible-max"
                    placeholder="$ Max"
                    formatOptions={{ style: "currency", currency: "USD", maximumFractionDigits: 0 }}
                    value={maxValue ? Number(maxValue) : undefined}
                    onChange={(n) => onMax(Number.isFinite(n) ? String(n) : "")}
                />
            </div>
        </Field>
    );
}
